// Currency context — auto-detects user's currency from IP, allows manual override.
// Caches detection in localStorage for 24h to avoid repeated IP lookups.
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { apiFetch } from "@/lib/api";

const STORAGE_KEY = "geovinum.currency.v1";
const TTL_MS = 24 * 60 * 60 * 1000; // 24h

const DEFAULT_RATES = {
  EUR: 1.0, USD: 1.08, GBP: 0.85, CAD: 1.47, CHF: 0.96,
  AUD: 1.62, SEK: 11.3, NOK: 11.5, DKK: 7.46,
};
const SUPPORTED = ["EUR", "USD", "GBP", "CAD", "CHF", "AUD", "SEK", "NOK", "DKK"];

const LOCALE_BY_CCY = {
  EUR: "fr-FR", USD: "en-US", GBP: "en-GB", CAD: "en-CA", CHF: "de-CH",
  AUD: "en-AU", SEK: "sv-SE", NOK: "nb-NO", DKK: "da-DK",
};

const CurrencyContext = createContext(null);

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.currency || !obj.detectedAt) return null;
    if (Date.now() - obj.detectedAt > TTL_MS) return null;
    return obj;
  } catch {
    return null;
  }
}

export const CurrencyProvider = ({ children }) => {
  const stored = loadStored();
  const [currency, setCurrencyState] = useState(stored?.currency || "EUR");
  const [rates, setRates] = useState(stored?.rates || DEFAULT_RATES);
  const [country, setCountry] = useState(stored?.country || "FR");
  const [autoDetected, setAutoDetected] = useState(!!stored?.autoDetected);

  // Auto-detect on first mount if we don't have a cached value
  useEffect(() => {
    if (stored) return; // Already cached, skip
    let cancelled = false;
    (async () => {
      const [detectRes, ratesRes] = await Promise.all([
        apiFetch(`/api/fx/detect`),
        apiFetch(`/api/fx/rates`),
      ]);
      if (cancelled) return;
      const detect = detectRes.ok ? detectRes.data : null;
      const ratesData = ratesRes.ok ? ratesRes.data : null;
      if (!detect && !ratesData) return; // keep defaults
      const ccy = SUPPORTED.includes(detect?.currency) ? detect.currency : "EUR";
      const next = {
        currency: ccy,
        country: detect?.country || "FR",
        rates: ratesData?.rates || DEFAULT_RATES,
        autoDetected: true,
        detectedAt: Date.now(),
      };
      setCurrencyState(next.currency);
      setRates(next.rates);
      setCountry(next.country);
      setAutoDetected(true);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const setCurrency = useCallback((ccy) => {
    if (!SUPPORTED.includes(ccy)) return;
    setCurrencyState(ccy);
    setAutoDetected(false);
    try {
      const cur = loadStored() || {};
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...cur, currency: ccy, autoDetected: false, detectedAt: Date.now(),
        rates: cur.rates || rates, country: cur.country || country,
      }));
    } catch { /* noop */ }
  }, [rates, country]);

  const value = useMemo(() => {
    const activeRate = rates[currency] || 1.0;
    const locale = LOCALE_BY_CCY[currency] || "fr-FR";
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });
    // Converts EUR cents → display string in active currency
    const formatFromEUR = (eurCents) => {
      if (eurCents == null) return "—";
      const amount = (eurCents / 100) * activeRate;
      return formatter.format(amount);
    };
    // Formats a cents amount that is ALREADY in a specific currency (no conversion)
    const formatCents = (cents, ccy) => {
      if (cents == null) return "—";
      const ccyCode = (ccy || currency).toUpperCase();
      const loc = LOCALE_BY_CCY[ccyCode] || "fr-FR";
      return new Intl.NumberFormat(loc, {
        style: "currency", currency: ccyCode, maximumFractionDigits: 0,
      }).format(cents / 100);
    };
    return {
      currency,
      country,
      rate: activeRate,
      rates,
      supported: SUPPORTED,
      autoDetected,
      setCurrency,
      formatFromEUR,
      formatCents,
      // Returns the converted amount in target-currency CENTS (rounded, integer)
      convertEURCents: (eurCents) => Math.round((eurCents || 0) * activeRate),
    };
  }, [currency, country, rates, autoDetected, setCurrency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Graceful fallback if used outside provider (during tests/storybook)
    return {
      currency: "EUR", country: "FR", rate: 1, rates: DEFAULT_RATES,
      supported: SUPPORTED, autoDetected: false, setCurrency: () => {},
      formatFromEUR: (c) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format((c || 0) / 100),
      formatCents: (c, ccy) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: ccy || "EUR", maximumFractionDigits: 0 }).format((c || 0) / 100),
      convertEURCents: (c) => c || 0,
    };
  }
  return ctx;
};
